import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { IInvoiceDataApiPort } from '@subscriptions/application/ports';
import type { UpdateInvoiceDataDto, InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { createInvoiceData } from '@subscriptions/domain/entities';
import { mapInvoiceDataToViewDto } from '@subscriptions/application/mappers';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for updating invoice data (datos fiscales).
 *
 * Flow: uuid + UpdateInvoiceDataDto → domain entity → update via port → InvoiceDataViewDto
 *
 * @param port - Invoice data API port
 * @returns Use case executor
 */
export const updateInvoiceDataUseCase = (port: IInvoiceDataApiPort) => {
  const execute = async (
    uuid: string,
    dto: UpdateInvoiceDataDto,
    includeDeleted = false,
  ): Promise<InvoiceDataViewDto> => {
    const span = tracer.startSpan('SUB.updateInvoiceData', {
      attributes: { 'invoice.uuid': uuid },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const invoiceData = createInvoiceData({
          uuid,
          businessName: dto.businessName,
          firstName: dto.firstName,
          firstLastName: dto.firstLastName,
          secondLastName: dto.secondLastName,
          postalCode: dto.postalCode,
          rfc: dto.rfc,
          cfdi: dto.cfdi,
          email: dto.email,
          taxRegime: dto.taxRegime,
        });

        const updated = await port.update(invoiceData, includeDeleted);
        const view = mapInvoiceDataToViewDto(updated);
        invoiceLog.info('updateInvoiceData', { 'invoice.dataUuid': uuid });
        return view;
      } catch (error) {
        invoiceLog.error('updateInvoiceData', {
          ...extractErrorAttributes(error),
          'invoice.dataUuid': uuid,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
